import { Link } from 'react-router-dom';
import { useMe } from '../hooks';
import { isIos } from '../lib/install';
import { PageHead } from '../ui';

/** Help: install as an app, push, e-mails, signing in. Also readable before signing in. */
export default function Hilfe() {
  const { data: me } = useMe();
  const ios = isIos();
  return (
    <div className="help">
      <PageHead title="Hilfe" />
      <nav className="help-toc" aria-label="Inhalt">
        <a href="#app">Als App installieren</a>
        <a href="#push">Push-Benachrichtigungen</a>
        <a href="#mail">E-Mails</a>
        <a href="#anmelden">Anmelden</a>
        <a href="#problem">Problem oder Idee?</a>
      </nav>

      <section className="panel" id="app">
        <h2>Als App installieren</h2>
        <p>
          Der Sportplaner lässt sich wie eine App auf den Startbildschirm legen: eigenes Symbol, ohne Adresszeile. Aus
          keinem App-Store – direkt aus dem Browser, kostenlos und ohne Konto.
        </p>
        <div className={`help-cols${ios ? ' ios-first' : ''}`}>
          <div>
            <h3>Android (Chrome, Samsung Internet)</h3>
            <ol>
              <li>
                <b>sport-planer.de/app</b> im Browser öffnen.
              </li>
              <li>
                Menü <b>⋮</b> → <b>„App installieren“</b> (oder „Zum Startbildschirm hinzufügen“). Bitte{' '}
                <b>„Installieren“</b> wählen, nicht „Verknüpfung erstellen“.
              </li>
              <li>Ab jetzt über das Vereinswappen auf dem Startbildschirm öffnen.</li>
            </ol>
          </div>
          <div>
            <h3>iPhone und iPad (Safari)</h3>
            <ol>
              <li>
                <b>sport-planer.de/app</b> in <b>Safari</b> öffnen.
              </li>
              <li>
                Unten auf das <b>Teilen-Symbol</b> tippen (Quadrat mit Pfeil nach oben).
              </li>
              <li>
                <b>„Zum Home-Bildschirm“</b> → „Hinzufügen“.
              </li>
              <li>Den Sportplaner ab jetzt über das neue Symbol öffnen und dort einmal anmelden.</li>
            </ol>
          </div>
          <div>
            <h3>Computer (Chrome, Edge)</h3>
            <ol>
              <li>
                In der Adresszeile rechts auf das Symbol <b>„App installieren“</b> klicken (Bildschirm mit Pfeil) – oder
                im Profil auf „Als App installieren“.
              </li>
            </ol>
            <p className="note">Firefox und Safari am Computer: einfach im Browser nutzen, ein Lesezeichen genügt.</p>
          </div>
        </div>
      </section>

      <section className="panel" id="push">
        <h2>Push-Benachrichtigungen</h2>
        <p>
          Neue und geänderte Termine, Flohmarkt-Anzeigen und Mitteilungen kommen auf Wunsch zusätzlich als Nachricht
          aufs Handy oder den Computer – so wie die E-Mails, nur schneller.
        </p>
        <ol>
          <li>
            Im <b>Profil</b> bei „Push auf diesem Gerät“ auf <b>„Auf diesem Gerät einschalten“</b> tippen und
            Benachrichtigungen <b>erlauben</b>.
          </li>
          <li>
            Mit <b>„Test senden“</b> prüfen: nach wenigen Sekunden kommt eine Nachricht.
          </li>
          <li>Darunter einstellen, wofür (Termine, Flohmarkt, Mitteilungen).</li>
        </ol>
        <ul>
          <li>
            <b>Jedes Gerät einzeln:</b> Handy und Computer (und jeder Browser) werden jeweils für sich eingeschaltet.
          </li>
          <li>
            <b>iPhone/iPad:</b> Push gibt es nur in der App vom Home-Bildschirm (siehe oben), nicht in Safari selbst.
          </li>
          <li>
            <b>Android und Computer:</b> klappt auch ohne Installation direkt im Browser.
          </li>
          <li>
            <b>Ausschalten:</b> im Profil „Ausschalten“ – oder „Push auf allen meinen Geräten ausschalten“, z. B. wenn
            ein Handy verloren ging. Beim Abmelden wird Push auf dem Gerät automatisch ausgeschaltet.
          </li>
          <li>
            <b>„Blockiert“?</b> Dann wurden Benachrichtigungen früher abgelehnt – in den Einstellungen des Browsers
            (Website-Einstellungen → Benachrichtigungen) wieder erlauben.
          </li>
          <li>Eigene Termine und Beiträge bekommst du nur, wenn im Profil „… auch an mich schicken“ an ist.</li>
        </ul>
      </section>

      <section className="panel" id="warteliste">
        <h2>Warteliste</h2>
        <ul>
          <li>
            Hat ein Termin begrenzte Plätze, gilt: <b>wer zuerst zusagt, ist dabei</b>. Alle weiteren stehen in der
            Reihenfolge ihrer Zusage auf der Warteliste.
          </li>
          <li>
            <b>Versehentlich abgesagt?</b> Dein Platz bleibt <b>5 Minuten</b> reserviert: „Rückgängig“ in der Meldung –
            oder einfach wieder „Dabei“ – und du hast deinen alten Platz zurück. Danach gilt eine neue Zusage als neue
            Anmeldung (hinten in der Reihe).
          </li>
          <li>
            <b>Wer nachrückt</b>, bekommt eine E-Mail und – wenn eingeschaltet – eine Push-Nachricht „Du bist dabei“,
            sobald der Platz sicher ist (nach einigen Minuten).
          </li>
          <li>
            Bei begrenzten Plätzen gibt es kein „Vielleicht“ – nur „Dabei“ oder „Nicht dabei“. Wer schon vorher
            „Vielleicht“ gesagt hatte, behält das, bis er zu- oder absagt; einen Platz hat „Vielleicht“ nicht.
          </li>
        </ul>
      </section>

      <section className="panel" id="mail">
        <h2>E-Mails</h2>
        <ul>
          <li>
            <b>E-Mails</b> zu Terminen, Flohmarkt und Mitteilungen bekommt, wer sich schon einmal angemeldet hat. Neue
            Termine gehen an alle; Änderungen, Absagen und Nachrichten nur an die Angemeldeten des Termins. Dazu kommen
            Antworten auf deine Kommentare, Nachrücken von der Warteliste und wenn die Orga dich ein- oder austrägt. Im
            Profil unter „Benachrichtigungen“ pro Bereich abschaltbar – oder über den Abmelde-Link unten in jeder Mail.
          </li>
          <li>
            <b>Ausgeschaltet</b> heißt: keine E-Mails aus dem Bereich – auch nicht zu deinen eigenen Anmeldungen oder
            Anzeigen. Nur Anmeldecodes und Einladungen kommen immer.
          </li>
        </ul>
      </section>

      <section className="panel" id="anmelden">
        <h2>Anmelden – ohne Passwort</h2>
        <ol>
          <li>Die E-Mail-Adresse eingeben, unter der du die Sportplaner-Nachrichten bekommst.</li>
          <li>
            Du bekommst eine E-Mail mit einem <b>6-stelligen Code</b> und einem <b>Anmelde-Link</b> (15 Minuten gültig).
            Code eintippen oder auf „Jetzt anmelden“ tippen – auf dem Gerät, auf dem du den Sportplaner nutzen willst.
          </li>
          <li>Du bleibst danach angemeldet und musst das nur selten wiederholen.</li>
        </ol>
        <p className="note">
          Keine E-Mail? Im Spam-Ordner nachsehen. Unbekannte Adresse oder neue Adresse? Bei der Abteilungsleitung
          melden.
        </p>
      </section>

      <section className="panel" id="problem">
        <h2>Problem oder Idee?</h2>
        <p>
          {me ? (
            <>
              Oben auf <b>Feedback</b> tippen und kurz beschreiben, was nicht klappt oder was du dir wünschst. Die
              Antwort erscheint später in deinem <Link to="/profil#feedback">Profil</Link>.
            </>
          ) : (
            <>
              Nach dem <Link to="/login">Anmelden</Link> oben auf <b>Feedback</b> tippen und kurz beschreiben, was nicht
              klappt.
            </>
          )}
        </p>
      </section>
    </div>
  );
}
